import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { GfTitleService } from '../../../services/core.title.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as _ from 'lodash';
import { UserRest } from '../../../services/core.user.rest.service';
import { UserService } from '../../../services/core.user.service';
import { LegacyKanbanService } from '../../../services/core.legacy-kanban.service';
import { GfTranslateService } from '../../../services/core.translate.service';
import { UiService } from '../../../services/core.ui.service';
import { NotificationService } from '../../../services/core.notification.service';
import { StateService } from '@uirouter/angular';

import { TrackeritemEditModalComponent } from '../../../../plugins/tracker/edit/trackerItemEditModal/trackeritem.edit.modal.component';

// Add interface for section types
interface Section {
  items: any[];
  pageSize: number;
}

@Component({
  selector: 'user-my-view',
  template: require('./user.my.component.html')
})
export class UserMyViewComponent implements OnInit, OnDestroy {

  @Input() user: any;

  expandedAct: any[] = [];
  trackerItemPageSize = 10;
  trackerItemPage = 1;
  queuePageSize = 10;
  queuePage = 1;
  activityPageSize = 20;
  activityPage = 1;

  trackerItemsAmount = 0;
  queueAmount = 0;
  activityAmount = 0;

  sameUser = false;
  queue: any[] = [];
  trackerItems: any[] = [];
  activity: any[] = [];

  selectedProjects: any[] = [];
  selectedTrackers: any[] = [];
  filteredTrackers: any[] = [];
  
  // Arrays to store original unfiltered data
  originalQueue: any[] = [];
  originalTrackerItems: any[] = [];

  // Drag & Drop state
  drag: {
    item: any;
    source: 'doing' | null;
    target: 'doing' | null;
    targetIndex: number;
  } = {
    item: null,
    source: null,
    target: null,
    targetIndex: -1
  };

  // Add section data property
  private sections: { [key: string]: Section } = {
    activity: {
      items: [],
      pageSize: 20
    }
  };

  projectsForDropdown: Array<{ id: any; label: string }> = [];
  trackersForDropdown: Array<{ id: any; label: string }> = [];

  constructor(
    private stateService: StateService,
    
    private userRest: UserRest,
    private userService: UserService,
    private legacyKanbanService: LegacyKanbanService,
    private titleService: GfTitleService,
    private translateService: GfTranslateService,
    private uiService: UiService,
    private modalService: NgbModal,
    private notificationService: NotificationService
  ) {}

  /**
   * Prepares tracker data for the dropdown (trackers grouped by project)
   */
  private prepareGroupedTrackerDropdown(): void {
    if (!this.user?.rel?.trackers) {
      this.trackersForDropdown = [];
      return;
    }

    // Use the conversion function for proper grouping
    const fieldOptions = this.convertTrackersToFieldOptions(this.user.rel.trackers);
    
    // Set the trackers dropdown options directly from fieldOptions
    this.trackersForDropdown = fieldOptions.fieldElements;
  }

  ngOnInit(): void {
    const unixName = this.stateService.params.unixName || this.user?.unixName;

    this.titleService.setTitle(this.user?.unixName || '', 'My Page');
    this.sameUser = (this.userService.session.getUnixName() === unixName);

    // Initialize columns from user relations
    this.originalQueue = _.get(this.user, 'rel.queue', []);
    this.originalTrackerItems = _.filter(
      _.get(this.user, 'rel.trackeritems', []),
      (item: any) => !_.find(this.originalQueue, { id: item.id })
    );
    
    this.queue = [...this.originalQueue];
    this.trackerItems = [...this.originalTrackerItems];
    
    this.activity = _.get(this.user, 'rel.activitylog', []);
    this.setColumnCounts();
    
    // Initialize trackers dropdown with grouped format
    this.prepareGroupedTrackerDropdown();

    this.projectsForDropdown = (this.user?.rel?.memberprojects ?? []).map((p: any) => ({
      id: p.id,
      label: p.name
    }));

    // Get selected trackers from state param, if any
    const trackerParam = this.stateService.params.trackers;
    if (trackerParam) {
      const trackerIds = trackerParam.split(',')
        .map(id => parseInt(id.trim(), 10))
        .filter(id => !isNaN(id));
      
      if (trackerIds.length > 0) {
        // Find trackers that match the IDs from the URL parameter
        this.selectedTrackers = trackerIds
          .map(id => {
            const tracker: any = this.trackersForDropdown.find(t => t.id === id);
            return tracker ? {
              id: tracker.id,
              label: tracker.label,
              group: tracker.group
            } : null;
          })
          .filter(t => t !== null);
        
        this.queue = [];
        this.trackerItems = [];
        
        this.loadQueue();
        this.loadTrackerItems();
      }
    }

    // Subscribe to notifications that update our columns
    this.notificationService.subscribe('my', 'queue-change', (data: any) => {
      this.queue = _.get(data, 'queue', this.queue);
      this.trackerItems = _.get(data, 'todoList', this.trackerItems);
      this.setColumnCounts();
    });
  }

  onProjectChange(projects: any[]): void {
    // Update selected projects
    this.selectedProjects = projects;
    
    // Reset tracker selections when changing projects
    if (this.selectedProjects.length === 0) {
      // If clearing all projects, keep tracker selections
    } else {
      // Filter selected trackers to only keep those from selected projects
      const selectedProjectIds = projects.map(project => String(project.id));
      this.selectedTrackers = this.selectedTrackers.filter(tracker => 
        selectedProjectIds.includes(String(tracker.projectId))
      );
    }
    
    // Update trackers dropdown
    this.updateTrackersList();
    
    // Clear existing items before loading new ones
    this.queue = [];
    this.trackerItems = [];
    
    // Reload data with new filters
    this.loadQueue();
    this.loadTrackerItems();
  }

  onTrackerChange(selectedItems: any[]): void {
    // Convert to internal format
    this.selectedTrackers = selectedItems ? selectedItems.map(item => ({
      id: item.id,
      label: item.label,
      group: item.group
    })) : [];
    
    this.selectedProjects = [];
    
    // Clear existing items before loading new ones
    this.queue = [];
    this.trackerItems = [];

    this.stateService.go('.', {
      trackers: this.selectedTrackers.map(t => t.id).join(','),
    }, { notify: false, location: 'replace', reload: false, inherit: true })
    .then(() => {
      this.loadQueue();
      this.loadTrackerItems();
    });
  }

  private updateTrackersList(): void {
    if (!this.user?.rel?.trackers) {
      this.filteredTrackers = [];
      this.trackersForDropdown = [];
      return;
    }
    
    if (this.selectedProjects && this.selectedProjects.length > 0) {
      // Get all project IDs from selected projects
      const selectedProjectIds = this.selectedProjects.map(project => String(project.id));
      
      // Filter trackers to show only those from selected projects
      this.filteredTrackers = this.user.rel.trackers.filter((tracker: any) => 
        selectedProjectIds.includes(String(tracker.project.id))
      );
    } else {
      // If no projects selected, show all trackers (don't unselect any tracker)
      this.filteredTrackers = this.user.rel.trackers;
    }
    
    this.updateTrackersForDropdown();
  }

  private updateTrackersForDropdown(): void {
    // Update trackers dropdown with formatted labels
    this.trackersForDropdown = (this.filteredTrackers ?? []).map((t: any) => ({
      id: t.id,
      projectId: t.project.id,
      label: "(" + t.project.name + ") " + t.trackerName
    }));
  }

  // Apply filters to kanban items
  private applyFilters(): void {
    this.queue = [...this.originalQueue];
    this.trackerItems = [...this.originalTrackerItems];
    
    // Apply project filter if no trackers are selected
    if (this.selectedProjects.length > 0 && this.selectedTrackers.length === 0) {
      // Get all project IDs from selected projects
      const selectedProjectIds = this.selectedProjects.map(project => project.id);
      
      // Filter queue and trackerItems by selected projects
      this.queue = this.queue.filter(item => selectedProjectIds.includes(item.projectId));
      this.trackerItems = this.trackerItems.filter(item => selectedProjectIds.includes(item.projectId));
    }
    
    // Apply tracker filter
    if (this.selectedTrackers.length > 0) {
      const selectedTrackerIds = this.selectedTrackers.map(tracker => tracker.id);
      this.queue = this.queue.filter(item => selectedTrackerIds.includes(item.trackerId));
      this.trackerItems = this.trackerItems.filter(item => selectedTrackerIds.includes(item.trackerId));
    }
    
    this.setColumnCounts();
  }

  ngOnDestroy(): void {
    this.notificationService.unsubscribe('my');
  }

  /* PAGINATION METHODS */
  setTodoPaging(params: any): void {
    this.trackerItemPageSize = params.pageSize;
    this.trackerItemPage = params.pageNum;
    this.loadTrackerItems();
  }

  setQueuePaging(params: any): void {
    this.queuePageSize = params.pageSize;
    this.queuePage = params.pageNum;
    this.loadQueue();
  }

  public changePage(group: string, params: any): void {
    switch (group) {
      case 'activity':
        this.activityPage = params.pageNum;
        this.activityPageSize = params.pageSize;
        this.loadActivity();
        break;
      default:
        break;
    }
  }

  showQuickEdit(trackeritem: any): void {
    const modalRef = this.modalService.open(TrackeritemEditModalComponent);
    modalRef.componentInstance.trackeritem = trackeritem;
    modalRef.componentInstance.tracker = trackeritem.tracker;
    modalRef.componentInstance.allowUpdate = trackeritem.canUpdate;
    modalRef.componentInstance.allowAssignment = trackeritem.canAssign;
    modalRef.componentInstance.close.subscribe(() => {
      modalRef.close();
    });
    modalRef.componentInstance.onDelete.subscribe((deletedTrackeritem: any) => {
      this.removeTrackeritemFromLists(deletedTrackeritem);
    });
  }

  goToTrackeritem(ti: any): void {
    this.stateService.go(
      'home.project.summary.tracker.item.edit',
      { id: ti.id, tracker: ti.tracker.id, unixName: ti.tracker.project.unixName }
    );
  }

  /* DRAG & DROP RELATED METHODS */
  /**
   * Triggered when dragging starts. We store which item is being dragged
   * and which list it came from.
   */
  onDragStart(event: DragEvent, item: any, source: 'doing'): void {
    this.drag.item = item;
    this.drag.source = source;
    // Some browsers might need setData to allow drag
    if (event.dataTransfer) {
      event.dataTransfer.setDragImage(event.currentTarget as any, 0, 0);
      event.dataTransfer.setData('text', String(item.id));
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  /**
   * Called while dragging over a valid drop zone.
   */
  onDragOver(event: DragEvent, target: 'doing'): void {
    if (!this.drag.item) return;
    event.preventDefault();

    this.drag.target = target;

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }

    const container = event.currentTarget as HTMLElement;
    const cards = Array.from(container.querySelectorAll('.kanban-card'));
    let dropIndex = cards.length;

    // Find the first card whose midpoint is below the cursor to compute insert position.
    for (let i = 0; i < cards.length; i++) {
      const rect = cards[i].getBoundingClientRect();
      if (rect.height === 0) continue;
      const midY = rect.top + rect.height / 2;
      if (event.clientY < midY) {
        dropIndex = i;
        break;
      }
    }

    this.drag.targetIndex = dropIndex;
  }

  /**
   * Called when the cursor leaves a drop zone container.
   * Clears the indicator only when truly leaving the container.
   */
  onDragLeave(event: DragEvent, column: 'doing'): void {
    const container = event.currentTarget as HTMLElement;
    const related = event.relatedTarget as HTMLElement;
    if (!related || !container.contains(related)) {
      if (this.drag.target === column) {
        this.drag.target = null;
        this.drag.targetIndex = -1;
      }
    }
  }

  isDraggingOver(column: 'doing'): boolean {
    return !!this.drag.item && this.drag.target === column;
  }

  /**
   * Called when the dragged item is dropped in a valid drop zone.
   * We have to check the source and target to decide if we are adding or removing from queue.
   */
  onDrop(event: DragEvent, target: 'doing'): void {
    event.preventDefault();

    if (!this.drag.item || !this.drag.source) {
      event.dataTransfer && (event.dataTransfer.effectAllowed = 'none');
      return;
    }

    const dropIndex = this.drag.targetIndex >= 0 ? this.drag.targetIndex : 0;

    // Same-column reorder in "Doing" — reorder queue
    if (target === 'doing' && this.drag.source === 'doing') {
      this.addToQueue(this.drag.item, dropIndex);
    }

    this.resetDrag();
  }

  /**
   * Called when the drag ends (even if not dropped in a valid zone).
   * Resets placeholders.
   */
  onDragEnd(event: DragEvent): void {
    event.stopPropagation();
    this.resetDrag();
  }

  private resetDrag(): void {
    this.drag = {
      item: null,
      source: null,
      target: null,
      targetIndex: -1
    };
  }

  /* LOGIC FOR ADDING/REMOVING QUEUE ITEMS -------------------- */
  addToQueue(item: any, index: number): void {
    const wasInQueue = this.queue.some(q => q.id === item.id);

    this.legacyKanbanService.addToQueue(
      this.user.unixName,
      this.trackerItems,
      this.queue,
      item,
      index
    );

    // Same-column reorder in Doing: keep backing queue arrays in sync.
    if (wasInQueue) {
      this.originalQueue = this.queue.map(q => {
        const fromOriginal = this.originalQueue.find(oq => oq.id === q.id);
        return fromOriginal || q;
      });
      this.applyFilters();
      return;
    }

    // Update original arrays
    const originalItem = this.originalTrackerItems.find(ti => ti.id === item.id);
    if (originalItem) {
      const originalIndex = this.originalTrackerItems.indexOf(originalItem);
      if (originalIndex > -1) {
        this.originalTrackerItems.splice(originalIndex, 1);
        this.originalQueue.push(originalItem);
      }
    }
    
    // Re-apply filters to maintain consistent view
    this.applyFilters();
  }

  removeFromQueue(item: any, index: number): void {
    this.legacyKanbanService.removeFromQueue(
      this.user.unixName,
      this.trackerItems,
      this.queue,
      item,
      index
    );

    // Update original arrays
    const originalItem = this.originalQueue.find(ti => ti.id === item.id);
    if (originalItem) {
      const originalIndex = this.originalQueue.indexOf(originalItem);
      if (originalIndex > -1) {
        this.originalQueue.splice(originalIndex, 1);
        this.originalTrackerItems.push(originalItem);
      }
    }
    
    // Re-apply filters to maintain consistent view
    this.applyFilters();
  }

  /**
   * Removes the deleted tracker item from all local collections
   */
  private removeTrackeritemFromLists(trackeritem: any): void {
    const trackeritemId = trackeritem?.id;
    if (!trackeritemId) {
      return;
    }

    const removeFromCollection = (collection?: any[]): boolean => {
      if (!Array.isArray(collection)) {
        return false;
      }

      const index = collection.findIndex(item => item?.id === trackeritemId);
      if (index > -1) {
        collection.splice(index, 1);
        return true;
      }

      return false;
    };

    const collections = [
      this.trackerItems,
      this.queue,
      this.originalTrackerItems,
      this.originalQueue,
      this.user?.rel?.trackeritems,
      this.user?.rel?.queue
    ];

    const removed = collections
      .map((collection) => removeFromCollection(collection))
      .some((result) => result);

    if (removed) {
      this.setColumnCounts();
    }
  }

  /* DATA LOADING LOGIC  */
  private setColumnCounts(): void {
    this.trackerItemsAmount = this.trackerItems?.length || 0;
    this.queueAmount = this.queue?.length || 0;
    this.activityAmount = this.activity?.length || 0;
  }

  private loadTrackerItems(): void {
    // Clear current items and show loading state
    this.trackerItems = [];
    
    // Prepare query parameters
    const params: any = {
      page_size: this.trackerItemPageSize,
      page_num: this.trackerItemPage,
      queue: false
    };
    
    // Add projects param if trackers are not selected
    if (this.selectedProjects.length > 0 && this.selectedTrackers.length === 0) {
      params.projects = this.selectedProjects.map(p => p.id).join(',');
    }
    
    // Add trackers param if selected
    if (this.selectedTrackers.length > 0) {
      params.trackers = this.selectedTrackers.map(t => t.id).join(',');
    }
    
    this.userRest.getTrackerItems(this.user.unixName, params).then(
      (result: any) => {
        // Store the items on the trackerItems array
        this.trackerItems = result.items || [];
        this.trackerItemsAmount = result.paging?.count || this.trackerItems.length;
        
        // Update the original array
        this.originalTrackerItems = [...this.trackerItems];
        
        // Items in the queue aren't shown in tracker items list
        if (this.queue && this.queue.length > 0) {
          const queueIds = this.queue.map(item => item.id);
          this.trackerItems = this.trackerItems.filter(item => !queueIds.includes(item.id));
        }
        
        // Update column counts
        this.setColumnCounts();
      },
      () => {
        const msg = this.translateService.translate('There was a problem loading assigned tickets');
        this.uiService.message.showWarning(msg, 'Data Error');
        this.trackerItems = [];
        this.originalTrackerItems = [];
        this.setColumnCounts();
      }
    );
  }

  private loadActivity(): void {
    this.activity = [];
    this.userRest.getUserActivity(this.user.unixName, {
      max: this.activityPageSize,
      page_num: this.activityPage,
      startDate: '1970',
      endDate: 'now'
    }).then(
      (resource: any) => {
        this.activity = resource.items;
      },
      () => {
        const msg = this.translateService.translate('There was a problem loading activity');
        this.uiService.message.showWarning(msg, 'Data Error');
        this.activity = [];
      }
    ).finally(() => this.setColumnCounts());
  }

  private loadQueue(): void {
    // Clear current items and show loading state
    this.queue = [];
    
    // Prepare query parameters
    const params: any = {
      page_size: this.queuePageSize,
      page_num: this.queuePage
    };
    
    // Add projects param if trackers are not selected
    if (this.selectedProjects.length > 0 && this.selectedTrackers.length === 0) {
      params.projects = this.selectedProjects.map(p => p.id).join(',');
    }
    
    // Add trackers param if selected
    if (this.selectedTrackers.length > 0) {
      params.trackers = this.selectedTrackers.map(t => t.id).join(',');
    }
    
    this.userRest.getQueue(this.user.unixName, params).then(
      (result: any) => {
        // Store the items directly on the queue array
        this.queue = result.items || [];
        this.queueAmount = result.paging?.count || this.queue.length;
        
        // Update the original array
        this.originalQueue = [...this.queue];
        
        // Update column counts
        this.setColumnCounts();
      },
      () => {
        const msg = this.translateService.translate('There was a problem loading To-Do');
        this.uiService.message.showWarning(msg, 'Data Error');
        this.queue = [];
        this.originalQueue = [];
        this.setColumnCounts();
      }
    );
  }

  hasNextPage(section: string): boolean {
    if (!this.sections[section]?.items) {
      return false;
    }

    const currentSection = this.sections[section];
    const totalItems = currentSection.items.length;
    const currentPage = this[`${section}Page`] || 1;
    const pageSize = this[`${section}PageSize`] || currentSection.pageSize;

    return currentPage * pageSize < totalItems;
  }

  /**
   * Converts tracker objects to the format required by the multi-select component
   * 
   * @param trackers - Array of trackers
   * @returns Object formatted for fieldOptions with fieldElements
   */
  private convertTrackersToFieldOptions(trackers: any[]): any {
    // Map each tracker to the required fieldElements format
    const fieldElements = trackers.map(tracker => ({
      id: tracker.id,
      label: tracker.trackerName || tracker.description,
      group: tracker.project?.name || 'Ungrouped',
      value: tracker.id,
      description: tracker.description
    }));

    return {
      fieldType: 1, // Field type 1 is "Select"
      fieldElements: fieldElements
    };
  }
}
